import { useCallback, useEffect, useState } from "react";
import { BellRing, Laptop, Loader2, Smartphone, Trash2 } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { disablePush, enablePush, pushState, thisDeviceToken, type PushState } from "@/lib/push";

interface Device {
  id: string;
  platform: string;
  device_name: string | null;
  last_used_at: string;
  last_error: string | null;
  token_tail: string;
}

const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 2 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

const HINT: Partial<Record<PushState, string>> = {
  unsupported: "This browser can't receive push notifications. Chrome, Edge, Firefox and Safari on a Home Screen app can.",
  "needs-install": "On an iPhone or iPad: tap Share → Add to Home Screen, open the CRM from the Home Screen icon, then come back here and turn notifications on.",
  "not-configured": "Push isn't set up yet. A Super Admin can do that in Settings → Push notifications.",
  denied: "Notifications are blocked for this site in your browser. Allow them in the site settings (the lock icon by the address), then reload.",
};

/** Turn push on for this device, see the devices that get it, send a test */
export function PushDevices({ onChange }: { onChange?: () => void }) {
  const { user } = useAuth();
  const { success, error } = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [busy, setBusy] = useState(false);
  const mine = thisDeviceToken();

  const load = useCallback(async () => {
    setState(await pushState());
    try {
      setDevices(unwrapArray<Device>(await api.get("/api/Push/Devices")));
    } catch {
      setDevices([]);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true);
    try {
      await fn();
      success(done);
      await load();
      onChange?.();
    } catch (e: any) {
      error("Push notifications", e?.message || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = unwrapObject<{ sent: number; failed: Array<{ device: string; error: string }> }>(await api.post("/api/Push/Test", {}));
      if (r.sent) success("Test sent", `It should pop up on ${r.sent} device${r.sent === 1 ? "" : "s"} in a few seconds. If nothing appears, check the browser's and the computer's notification settings.`);
      else error("No device took it", r.failed?.[0]?.error || "The notification service didn't accept the test. Try turning push off and on again for this device.");
      await load();
    } catch (e: any) {
      error("Test failed", e?.message || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Push notifications on your devices" className="flex flex-col gap-2 rounded-[10px] border border-border bg-card px-3.5 py-3">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><BellRing className="size-4 text-primary" /> Push notifications</h2>
      <p className="text-xs text-muted-foreground">A pop-up on your phone or computer for what can't wait, like a missed call or a quote waiting for you, even when the CRM is closed.</p>

      {state === null ? (
        <p className="py-2 text-xs text-muted-foreground">Checking…</p>
      ) : state === "on" ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-success"><span className="size-2 rounded-full bg-success" /> On for this device</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={test}>{busy ? <Loader2 className="size-3.5 animate-spin" /> : null} Send a test</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => disablePush(String(user?.Id ?? "")), "Turned off for this device")}>Turn off here</Button>
        </div>
      ) : state === "off" ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy} onClick={() => run(() => enablePush(String(user?.Id ?? "")), "Turned on for this device")}>{busy ? <Loader2 className="size-3.5 animate-spin" /> : <BellRing className="size-3.5" />} Turn on for this device</Button>
          <span className="text-xs text-muted-foreground">Your browser will ask for permission.</span>
        </div>
      ) : (
        <p className="rounded-md bg-muted px-2.5 py-2 text-xs text-muted-foreground">{HINT[state]}</p>
      )}

      {devices.length > 0 && (
        <div className="flex flex-col divide-y divide-border/60 rounded-md border border-border/60">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center gap-2.5 px-2.5 py-2 text-[12.5px]">
              {d.platform === "web" ? <Laptop className="size-4 shrink-0 text-muted-foreground" /> : <Smartphone className="size-4 shrink-0 text-muted-foreground" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">{d.device_name || d.platform}{mine && mine.endsWith(d.token_tail) ? " (this device)" : ""}</span>
                <span className="block text-[11.5px] text-muted-foreground">Last used {ago(d.last_used_at)}{d.last_error ? ` · ${d.last_error}` : ""}</span>
              </span>
              <button type="button" aria-label={`Remove ${d.device_name || "device"}`} title="Remove" disabled={busy} onClick={() => run(async () => { await api.delete(`/api/Push/Devices/${d.id}`); if (mine && mine.endsWith(d.token_tail)) await disablePush(String(user?.Id ?? "")); }, "Device removed")} className="text-muted-foreground hover:text-danger disabled:opacity-40">
                <Trash2 className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
